import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
  PropsWithChildren,
  useMemo,
  useRef,
} from 'react'

import { AlmanacFileSummary, MediaObject, UserRole } from 'types'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import useInitializeContext, { useAuthedArticle, useCreateOrUpdateScheduledPost } from './hooks'
import { observer } from 'mobx-react-lite'
import { UserIntent } from 'components/PostStatusDropdown/Types'

import { ScheduledPostErrorType, ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { useRootStore } from 'stores/RootStore'
import { getPlatformValidators } from './validators'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { usePostProperties } from './usePostProperties'
import { useAutoSave } from './useAutoSave'
import { DesignChannel } from 'components/NewFileModal/Types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import caslAbility from 'utils/caslAbility'
import { useCurrentArticleMeta } from 'hooks/useCurrentArticleMeta'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { getProviderForChannel } from './utils'

interface ArticlePostPreviewState {
  content: string
  integration?: ExternalIntegration
  files: MediaObject[]
  expanded: boolean
  channel?: ChannelType
  status: { value: string; id: string | number } | undefined
  loading: boolean
  userIntent: UserIntent
  editing: boolean
  changingCopy: boolean
  newCopy: string
  oldCopy: string
  initialContent: string
  initialFiles: MediaObject[]
  errorMessage?: string
  isDirty: boolean
  postAt?: string
  errorType?: ScheduledPostErrorType
  lastAutoSave?: Date
  scratchPadOpen: boolean
  submitAttempted: boolean
  uploadingMediaFilesMetadata: MediaFileMetadata[]
  initialized: boolean
  canEdit?: boolean
}

export interface MediaFileMetadata {
  size?: number
  width?: number
  height?: number
  duration?: number
  frame_rate?: number
  aspect_ratio?: number
  thumbnail_url?: string
  progress?: number
}

export enum ArticlePostPreviewActionTypes {
  SET_CONTENT = 'SET_CONTENT',
  SET_FILES = 'SET_FILES',
  TOGGLE_EXPAND = 'TOGGLE_EXPAND',
  SET_LOADING = 'SET_LOADING',
  SET_USER_INTENT = 'SET_USER_INTENT',
  SET_LAST_AUTO_SAVE = 'SET_LAST_AUTO_SAVE',
  SET_EDITING = 'SET_EDITING',
  SET_CHANGING_COPY = 'SET_CHANGING_COPY',
  SET_NEW_COPY = 'SET_NEW_COPY',
  SET_OLD_COPY = 'SET_OLD_COPY',
  SET_UPLOADING_MEDIA_FILE_PROGRESS = 'SET_UPLOADING_MEDIA_FILE_PROGRESS',
  SET_UPLOADING_MEDIA_FILES_METADATA = 'SET_UPLOADING_MEDIA_FILES_METADATA',
  SET_SCRATCHPAD_OPEN = 'SET_SCRATCHPAD_OPEN',
  SET_SUBMIT_ATTEMPTED = 'SET_SUBMIT_ATTEMPTED',
  PERSIST_CHANGES = 'PERSIST_CHANGES',
  INITIALIZE = 'INITIALIZE',
  RESET = 'RESET',
}

export const maxFiles: Record<ExternalIntegrationEnum, number> = {
  [ExternalIntegrationEnum.LinkedIn]: 20,
  [ExternalIntegrationEnum.X]: 4,
  [ExternalIntegrationEnum.Instagram]: 10,
  [ExternalIntegrationEnum.Facebook]: 10,
  [ExternalIntegrationEnum.WordPress]: 1,
  [ExternalIntegrationEnum.TikTok]: 1,
  // TODO: set real values for YouTube with BLZ-3298
  [ExternalIntegrationEnum.YouTube]: 1,
  [ExternalIntegrationEnum.Mailchimp]: 0,
  [ExternalIntegrationEnum.Google]: 0,
  [ExternalIntegrationEnum.GoogleDrive]: 0,
  [ExternalIntegrationEnum.GoogleBusinessProfile]: 0,
  [ExternalIntegrationEnum.MetaAds]: 0,
  [ExternalIntegrationEnum.GoogleAds]: 0,
  [ExternalIntegrationEnum.GoHighLevel]: 1,
  [ExternalIntegrationEnum.Wix]: 1,
  [ExternalIntegrationEnum.Shopify]: 1,
}
export const maxFileSize: Record<ExternalIntegrationEnum, number> = {
  [ExternalIntegrationEnum.LinkedIn]: 10 * 1024 * 1024, // 10mb
  [ExternalIntegrationEnum.X]: 5 * 1024 * 1024, // 5mb
  [ExternalIntegrationEnum.Instagram]: 8 * 1024 * 1024, // 8mb
  [ExternalIntegrationEnum.Facebook]: 10 * 1024 * 1024, // 10mb
  [ExternalIntegrationEnum.WordPress]: Infinity, // No limit, depends on the server
  [ExternalIntegrationEnum.TikTok]: 20 * 1024 * 1024, // 20mb
  // TODO: set real values for YouTube with BLZ-3298
  [ExternalIntegrationEnum.YouTube]: 4 * 1024 * 1024 * 1024, // 4gb
  [ExternalIntegrationEnum.Mailchimp]: 0,
  [ExternalIntegrationEnum.Google]: 0,
  [ExternalIntegrationEnum.GoogleDrive]: 0,
  [ExternalIntegrationEnum.GoogleBusinessProfile]: 0,
  [ExternalIntegrationEnum.MetaAds]: 0,
  [ExternalIntegrationEnum.GoogleAds]: 0,
  [ExternalIntegrationEnum.GoHighLevel]: Infinity,
  [ExternalIntegrationEnum.Wix]: Infinity,
  [ExternalIntegrationEnum.Shopify]: Infinity,
}
export const maxVideoSize: Record<ExternalIntegrationEnum, number> = {
  [ExternalIntegrationEnum.LinkedIn]: 500 * 1024 * 1024, // 500mb
  [ExternalIntegrationEnum.X]: 512 * 1024 * 1024, // 512mb
  [ExternalIntegrationEnum.Instagram]: 4 * 1024 * 1024 * 1024, // 4gb
  [ExternalIntegrationEnum.Facebook]: 10 * 1024 * 1024 * 1024, // 10gb
  [ExternalIntegrationEnum.WordPress]: Infinity, // No limit, depends on the server
  [ExternalIntegrationEnum.TikTok]: 4 * 1024 * 1024 * 1024, // 4gb
  // TODO: set real values for YouTube with BLZ-3298
  [ExternalIntegrationEnum.YouTube]: 4 * 1024 * 1024 * 1024, // 4gb
  [ExternalIntegrationEnum.Mailchimp]: 0,
  [ExternalIntegrationEnum.Google]: 0,
  [ExternalIntegrationEnum.GoogleDrive]: 0,
  [ExternalIntegrationEnum.GoogleBusinessProfile]: 0,
  [ExternalIntegrationEnum.MetaAds]: 0,
  [ExternalIntegrationEnum.GoogleAds]: 0,
  [ExternalIntegrationEnum.GoHighLevel]: Infinity,
  [ExternalIntegrationEnum.Wix]: Infinity,
  [ExternalIntegrationEnum.Shopify]: Infinity,
}
export const maxCharacters: Record<ExternalIntegrationEnum, number> = {
  [ExternalIntegrationEnum.LinkedIn]: 3000,
  [ExternalIntegrationEnum.X]: 280,
  [ExternalIntegrationEnum.Instagram]: 2200,
  [ExternalIntegrationEnum.Facebook]: 33_000,
  [ExternalIntegrationEnum.WordPress]: Infinity,
  [ExternalIntegrationEnum.TikTok]: 2200,
  [ExternalIntegrationEnum.YouTube]: 5000,
  [ExternalIntegrationEnum.Mailchimp]: 10000,
  [ExternalIntegrationEnum.Google]: 0,
  [ExternalIntegrationEnum.GoogleDrive]: 0,
  [ExternalIntegrationEnum.GoogleBusinessProfile]: 1500,
  [ExternalIntegrationEnum.MetaAds]: 0,
  [ExternalIntegrationEnum.GoogleAds]: 0,
  [ExternalIntegrationEnum.GoHighLevel]: Infinity,
  [ExternalIntegrationEnum.Wix]: Infinity,
  [ExternalIntegrationEnum.Shopify]: Infinity,
}

export const providerFromChannelType: Partial<
  Record<ChannelType, ExternalIntegrationEnum> & Record<DesignChannel, ExternalIntegrationEnum>
> = {
  facebook_post: ExternalIntegrationEnum.Facebook,
  [ChannelType.InstagramPosts]: ExternalIntegrationEnum.Instagram,
  [ChannelType.InstagramReel]: ExternalIntegrationEnum.Instagram,
  [ChannelType.LinkedInPosts]: ExternalIntegrationEnum.LinkedIn,
  [ChannelType.Tweets]: ExternalIntegrationEnum.X,
  [ChannelType.TweetThread]: ExternalIntegrationEnum.X,
  [ChannelType.BlogPost]: ExternalIntegrationEnum.WordPress,
  [ChannelType.TikTokAds]: ExternalIntegrationEnum.TikTok,
  [ChannelType.TikTokScript]: ExternalIntegrationEnum.TikTok,
  [ChannelType.Newsletter]: ExternalIntegrationEnum.Mailchimp,
  [DesignChannel.InstagramPost]: ExternalIntegrationEnum.Instagram,
  [DesignChannel.InstagramReel]: ExternalIntegrationEnum.Instagram,
  [DesignChannel.InstagramStory]: ExternalIntegrationEnum.Instagram,
  [DesignChannel.TwitterPost]: ExternalIntegrationEnum.X,
  [DesignChannel.LinkedInPost]: ExternalIntegrationEnum.LinkedIn,
  [DesignChannel.TikTokPost]: ExternalIntegrationEnum.TikTok,
  [DesignChannel.YoutubeShort]: ExternalIntegrationEnum.YouTube,
  [DesignChannel.GoogleMyBusinessPost]: ExternalIntegrationEnum.GoogleBusinessProfile,
}

export const channelTypeFromProvider: Partial<Record<ExternalIntegrationEnum, ChannelType | DesignChannel>> = {
  [ExternalIntegrationEnum.Facebook]: ChannelType.FacebookPost,
  [ExternalIntegrationEnum.Instagram]: ChannelType.InstagramPosts,
  [ExternalIntegrationEnum.LinkedIn]: ChannelType.LinkedInPosts,
  [ExternalIntegrationEnum.X]: ChannelType.Tweets,
  [ExternalIntegrationEnum.WordPress]: ChannelType.BlogPost,
  [ExternalIntegrationEnum.TikTok]: DesignChannel.TikTokPost,
  [ExternalIntegrationEnum.YouTube]: DesignChannel.YoutubeShort,
  [ExternalIntegrationEnum.GoHighLevel]: ChannelType.BlogPost,
  [ExternalIntegrationEnum.Wix]: ChannelType.BlogPost,
  [ExternalIntegrationEnum.Shopify]: ChannelType.BlogPost,
  [ExternalIntegrationEnum.GoogleBusinessProfile]: DesignChannel.GoogleMyBusinessPost,
}

export type ArticlePostPreviewAction =
  | { type: ArticlePostPreviewActionTypes.SET_CONTENT; payload: string }
  | { type: ArticlePostPreviewActionTypes.SET_FILES; payload: MediaObject[] }
  | { type: ArticlePostPreviewActionTypes.TOGGLE_EXPAND }
  | { type: ArticlePostPreviewActionTypes.SET_LOADING; payload: boolean }
  | { type: ArticlePostPreviewActionTypes.SET_USER_INTENT; payload: UserIntent }
  | { type: ArticlePostPreviewActionTypes.SET_EDITING; payload: boolean }
  | { type: ArticlePostPreviewActionTypes.SET_CHANGING_COPY; payload: boolean }
  | { type: ArticlePostPreviewActionTypes.SET_NEW_COPY; payload: string }
  | { type: ArticlePostPreviewActionTypes.SET_OLD_COPY; payload: string }
  | { type: ArticlePostPreviewActionTypes.SET_UPLOADING_MEDIA_FILE_PROGRESS; payload: number }
  | { type: ArticlePostPreviewActionTypes.SET_UPLOADING_MEDIA_FILES_METADATA; payload: MediaFileMetadata[] }
  | { type: ArticlePostPreviewActionTypes.SET_LAST_AUTO_SAVE; payload: Date }
  | { type: ArticlePostPreviewActionTypes.SET_SCRATCHPAD_OPEN; payload: boolean }
  | { type: ArticlePostPreviewActionTypes.SET_SUBMIT_ATTEMPTED; payload: boolean }
  | { type: ArticlePostPreviewActionTypes.RESET }
  | { type: ArticlePostPreviewActionTypes.PERSIST_CHANGES; payload: { content: string; files: MediaObject[] } }
  | {
      type: ArticlePostPreviewActionTypes.INITIALIZE
      payload: {
        content: string
        files: MediaObject[]
        loading: boolean
        initialContent: string
        integration?: ExternalIntegration
        initialFiles: MediaObject[]
        errorMessage?: string
        errorType?: ScheduledPostErrorType
        editing: boolean
        changingCopy: boolean
        newCopy: string
        oldCopy: string
        channel?: ChannelType
        uploadingMediaFilesMetadata: MediaFileMetadata[]
        status?: { value: string; id: number | string }
      }
    }

const initialState: ArticlePostPreviewState = {
  content: '',
  integration: undefined,
  files: [],
  expanded: true,
  channel: undefined,
  status: undefined,
  loading: true,
  userIntent: UserIntent.SaveAsDraft,
  editing: false,
  changingCopy: false,
  newCopy: '',
  oldCopy: '',
  initialContent: '',
  initialFiles: [],
  isDirty: false,
  lastAutoSave: undefined,
  scratchPadOpen: false,
  submitAttempted: false,
  uploadingMediaFilesMetadata: [],
  initialized: false,
}

const ArticlePostPreviewContext = createContext<{
  state: ArticlePostPreviewState
  dispatch: React.Dispatch<ArticlePostPreviewAction>
  validationErrors: string[]
  almanacFile: Omit<AlmanacFileSummary, 'ancestors'>
  persistChanges: () => Promise<void>
  scheduledPost: ScheduledPostShow | undefined
  canEdit?: boolean
  updateContent: (content: string) => void
}>({
  state: initialState,
  dispatch: () => undefined,
  validationErrors: [],
  persistChanges: () => Promise.resolve(),
  almanacFile: {} as AlmanacFileSummary,
  scheduledPost: undefined,
  canEdit: true,
  updateContent: () => Promise.resolve(),
})

function areFilesEqual(files1: MediaObject[], files2: MediaObject[]): boolean {
  if (files1.length !== files2.length) return false
  return files1.every((file, index) => file.id === files2[index].id)
}

const reducer = (state: ArticlePostPreviewState, action: ArticlePostPreviewAction): ArticlePostPreviewState => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        ...state,
        ...action.payload,
        initialized: true,
      }
    case 'SET_CONTENT':
      return {
        ...state,
        content: action.payload,
        isDirty: action.payload !== state.initialContent,
      }
    case 'SET_FILES':
      return { ...state, files: action.payload, isDirty: !areFilesEqual(action.payload, state.initialFiles) }
    case 'TOGGLE_EXPAND':
      return { ...state, expanded: !state.expanded }
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_USER_INTENT':
      return { ...state, userIntent: action.payload }
    case 'SET_EDITING':
      return { ...state, editing: action.payload, expanded: true }
    case 'SET_CHANGING_COPY':
      return { ...state, changingCopy: action.payload }
    case 'SET_NEW_COPY':
      return { ...state, newCopy: action.payload }
    case 'SET_OLD_COPY':
      return { ...state, oldCopy: action.payload }
    case 'SET_UPLOADING_MEDIA_FILE_PROGRESS':
      const newMetadata = [...state.uploadingMediaFilesMetadata]
      const metadataIndex = newMetadata.findIndex((md) => md.progress !== undefined && md.progress < 100)

      if (metadataIndex < 0) {
        return { ...state }
      } else {
        newMetadata[metadataIndex] = {
          ...newMetadata[metadataIndex],
          progress: action.payload,
        }
      }

      return { ...state, uploadingMediaFilesMetadata: newMetadata }
    case 'SET_UPLOADING_MEDIA_FILES_METADATA':
      return { ...state, uploadingMediaFilesMetadata: action.payload }
    case 'SET_LAST_AUTO_SAVE':
      return { ...state, lastAutoSave: action.payload }
    case 'SET_SCRATCHPAD_OPEN':
      return { ...state, scratchPadOpen: action.payload }
    case 'SET_SUBMIT_ATTEMPTED':
      return { ...state, submitAttempted: action.payload }
    case 'PERSIST_CHANGES':
      return {
        ...state,
        initialContent: action.payload.content,
        initialFiles: action.payload.files,
        isDirty: false,
        editing: false,
        changingCopy: false,
        newCopy: '',
        oldCopy: '',
        uploadingMediaFilesMetadata: [],
      }
    case 'RESET':
      return {
        ...state,
        content: state.initialContent,
        files: state.initialFiles,
        isDirty: false,
        editing: false,
        changingCopy: false,
        newCopy: '',
        oldCopy: '',
        uploadingMediaFilesMetadata: [],
      }
    default:
      throw new Error(`Unhandled action type`)
  }
}

interface ArticlePostPreviewProviderProps
  extends PropsWithChildren<{
    children: ReactNode
    almanacFile: AlmanacFileSummary
  }> {}

interface ArticlePostPreviewProviderContainerProps
  extends PropsWithChildren<{
    children: ReactNode
    fileId: string
  }> {}

const ArticlePostPreviewProviderContainer = observer(
  ({ children, fileId }: ArticlePostPreviewProviderContainerProps) => {
    const { files } = useRootStore()
    const almanacFile = files.get(fileId)
    useEffect(() => {
      files.fetch(fileId)
    }, [files, fileId])

    if (!almanacFile) {
      return null
    }

    return <ArticlePostPreviewProvider almanacFile={almanacFile}>{children}</ArticlePostPreviewProvider>
  },
)

const ArticlePostPreviewProvider: React.FC<ArticlePostPreviewProviderProps> = observer(({ children, almanacFile }) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { data: scheduledPost } = useScheduledPost({ id: almanacFile.scheduled_post_id })
  const { status } = usePostProperties({ id: almanacFile.id, type: AssigneeType.Article })
  const authedArticle = useAuthedArticle(almanacFile)
  const articleMeta = useCurrentArticleMeta()
  const workspace = useCurrentWorkspace()

  const hasInitializedWithContent = useRef(false)
  const previousContent = useRef<string>('')

  const effectiveChannel = state.channel || almanacFile.channel

  const provider = effectiveChannel
    ? getProviderForChannel(effectiveChannel, scheduledPost?.external_integration)
    : undefined
  const validators = provider && effectiveChannel ? getPlatformValidators(provider, effectiveChannel) : null

  const { createOrUpdateScheduledPost } = useCreateOrUpdateScheduledPost({
    almanacFile,
    provider,
  })

  const currentWorkspaceId = useCurrentWorkspaceId()

  useInitializeContext({
    dispatch,
    almanacFile,
    initialized: state.initialized,
  })

  const canEdit = useMemo(() => {
    if (!currentWorkspaceId || !authedArticle) return false
    if (articleMeta?.main_layer.public && workspace?.role === UserRole.UNAUTHED) return false

    return caslAbility.can('edit', authedArticle)
  }, [currentWorkspaceId, authedArticle, articleMeta?.main_layer.public, workspace?.role])

  useEffect(() => {
    if (!state.initialized) return

    dispatch({ type: ArticlePostPreviewActionTypes.SET_FILES, payload: scheduledPost?.media_objects || [] })
  }, [scheduledPost?.media_objects, state.initialized])

  const persistChanges = async () => {
    if (!canEdit) return
    let newAlternativeOptions = scheduledPost?.alternative_options
    if (state.newCopy) {
      const index = newAlternativeOptions?.indexOf(state.newCopy)
      if (newAlternativeOptions && index !== undefined && index > -1) newAlternativeOptions[index] = state.oldCopy
    }

    const newContent = state.newCopy || state.content

    await createOrUpdateScheduledPost({
      content: newContent,
      postData: {},
      mediaObjectIds: state.files.map((file) => file.id),
      sourceId: almanacFile.id,
      sourceType: almanacFile.asset_type as string,
      alternativeOptions: newAlternativeOptions,
    })
    dispatch({
      type: ArticlePostPreviewActionTypes.PERSIST_CHANGES,
      payload: { content: newContent, files: state.files },
    })
  }

  const autoSaveChanges = () => {
    if (!canEdit) return

    if (!hasInitializedWithContent.current && !state.content) {
      return
    }

    createOrUpdateScheduledPost({
      content: state.content,
    }).then(() => {
      dispatch({ type: ArticlePostPreviewActionTypes.SET_LAST_AUTO_SAVE, payload: new Date() })
    })
  }

  const updateContent = (content: string) => {
    if (!canEdit) return

    createOrUpdateScheduledPost({
      content,
    })
    dispatch({ type: ArticlePostPreviewActionTypes.SET_CONTENT, payload: content })
  }

  useEffect(() => {
    if (state.initialized && state.initialContent && !hasInitializedWithContent.current) {
      hasInitializedWithContent.current = true
      previousContent.current = state.initialContent
    }
  }, [state.initialized, state.initialContent])

  // Remove files from the autosave logic
  const autoSave = useAutoSave(autoSaveChanges, [state.content], {
    saveOnNavigate: status?.value === PostStatusPropertyValue.Draft,
  })

  // Trigger an immediate save whenever files are changed
  useEffect(() => {
    if (!canEdit) return
    if (state.files.length > 0 && state.initialized) {
      createOrUpdateScheduledPost({
        mediaObjectIds: state.files.map((file) => file.id),
      }).then(() => {
        dispatch({ type: ArticlePostPreviewActionTypes.SET_LAST_AUTO_SAVE, payload: new Date() })
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.files, state.initialized, canEdit])

  useEffect(() => {
    if (status?.value !== PostStatusPropertyValue.Draft || !state.initialized) return

    const shouldAutoSave =
      state.content &&
      (hasInitializedWithContent.current || state.content !== '') &&
      state.content !== previousContent.current

    if (shouldAutoSave) {
      previousContent.current = state.content
      autoSave()
    }
  }, [state.content, status?.value, state.initialized, autoSave])

  const validationErrors = useMemo(
    () =>
      validators
        ? [
            ...validators.validateFiles(state.files),
            ...validators.validateContent(state.content),
            ...validators.validateVideosSize(state.files),
            ...validators.validateImagesSize(state.files),
            ...validators.validateVideosDuration(state.files),
            ...validators.validateVideosAspectRatio(state.files),
            ...validators.validateVideosFormat(state.files),
            ...validators.validateVideosCount(state.files),
            ...(validators.validateVideosWidth ? validators.validateVideosWidth(state.files) : []),
            ...(validators.validateVideosFrameRate ? validators.validateVideosFrameRate(state.files) : []),
          ]
        : [],
    [validators, state.files, state.content],
  )

  useEffect(() => {
    if (state.submitAttempted && validationErrors.length === 0) {
      dispatch({ type: ArticlePostPreviewActionTypes.SET_SUBMIT_ATTEMPTED, payload: false })
    }
  }, [validationErrors, state.submitAttempted])

  return (
    <ArticlePostPreviewContext.Provider
      value={{
        state: { ...state, channel: almanacFile.channel as ChannelType, status },
        dispatch,
        validationErrors,
        persistChanges,
        almanacFile,
        scheduledPost: scheduledPost,
        canEdit,
        updateContent,
      }}
    >
      {children}
    </ArticlePostPreviewContext.Provider>
  )
})

export { ArticlePostPreviewProviderContainer as ArticlePostPreviewProvider }

export const useArticlePostPreviewContext = (): {
  state: ArticlePostPreviewState
  dispatch: React.Dispatch<ArticlePostPreviewAction>
  validationErrors: string[]
  persistChanges: () => Promise<void>
  almanacFile: Omit<AlmanacFileSummary, 'ancestors'>
  scheduledPost: ScheduledPostShow | undefined
  canEdit?: boolean
  updateContent: (content: string) => void
} => {
  const context = useContext(ArticlePostPreviewContext)
  if (!context) {
    throw new Error('useArticlePostPreviewContext must be used within a ArticlePostPreviewProvider')
  }
  return context
}
